import type {
	FRAME_RESOURCE_KEYS,
	LOCALE_SELECT_RESOURCE_KEYS
} from "@com.mgmtp.a12.client/client-core/lib/core/frame";
import type { LOCALE_RESOURCE_KEYS } from "@com.mgmtp.a12.client/client-core/lib/core/locale";
import type { DIRTY_HANDLING_RESOURCE_KEYS } from "@com.mgmtp.a12.client/client-core/lib/extensions/dirtyHandling";
import type { HETEROGENEITY_RESOURCE_KEYS } from "@com.mgmtp.a12.client/client-core/lib/extensions/heterogeneity";
import type { CRUD_RESOURCE_KEYS } from "@com.mgmtp.a12.crud/crud-core";
import type { RESOURCE_KEYS as FORMENGINE_RESOURCE_KEYS } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/localization";
import type { RESOURCE_KEYS as OVERVIEWENGINE_RESOURCE_KEYS } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/services/localization";
import type { RESOURCE_KEYS as TREEENGINE_RESOURCE_KEYS } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/core/services/localization";
import { initializeKeys } from "@com.mgmtp.a12.utils/utils-localization/lib/main";

/** This map should only contain custom keys. */
export const RESOURCE_KEYS = {
	application: {
		brand: "",
		title: "",
		documentTitle: "",
		titleParts: {
			prefix: "",
			main: ""
		},
		shortTitle: "",
		header: {
			title: {
				backTitle: ""
			},
			user: {
				title: "",
				item: {
					user: { label: "" },
					roles: {
						label: { single: "", multiple: "" },
						type: {
							admin: "",
							fv_admin: "",
							fv_manager: "",
							fv_designer: "",
							none: ""
						},
						composition: {
							client: ""
						}
					},
					virtualScrolling: { label: "", hint: "" }
				},
				button: { logout: { label: "" } }
			},
			banner: {
				stage: ""
			},
			menu: {
				title: "",
				label: ""
			},
			info: {
				title: "",
				version: { label: "" },
				documents: { label: "", accessibilityLabel: "" },
				accessibility: { label: "" }
			}
		},
		view: {
			moduleMoveModal: {
				title: "",
				description: {
					info: "",
					hint: ""
				},
				button: {
					append: "",
					replace: ""
				}
			},
			noAccess: {
				button: { label: "" },
				message: ""
			},
			accessibility: {
				title: "",
				button: { close: { title: "" } }
			},
			logout: {
				button: { label: "" },
				message: ""
			},
			loginError: {
				button: { label: "" },
				message: ""
			},
			fachverfahrenOverview: {
				documentTitle: {
					FACHVERFAHREN: "",
					STAMMDATEN: ""
				},
				fachverfahrenKey: {
					description: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					}
				},
				preview: {
					title: ""
				},
				internalApplication: {
					icon: {
						active: { title: "" },
						inactive: { title: "" }
					}
				},
				status: {
					job: {
						PUBLISH: { label: "" },
						UPDATE: { label: "" },
						PREVIEW: { label: "" },
						DELETE: { label: "" }
					},
					lost: {
						description: {
							FACHVERFAHREN: "",
							STAMMDATEN: ""
						}
					}
				},
				version: "",
				rowAction: {
					open: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					}
				}
			},
			printTemplateOverview: {
				documentTitle: "",
				rowAction: {
					open: ""
				}
			},
			fachverfahrenTree: {
				title: {
					FACHVERFAHREN: "",
					STAMMDATEN: ""
				},
				subtitle: {
					default: "",
					new: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					}
				},
				rootNode: {
					FACHVERFAHREN: "",
					STAMMDATEN: ""
				},
				module: {
					placeholder: ""
				},
				dockingPoint: {
					anonymous: "",
					unknown: ""
				},
				button: {
					add: { title: "" },
					close: {
						title: {
							FACHVERFAHREN: "",
							STAMMDATEN: ""
						}
					}
				},
				contextMenu: {
					button: { title: "" },
					subHeader: ""
				},
				validationBar: {
					migration: {
						title: "",
						description: {
							general: "",
							autoMigrationPossible: ""
						},
						button: {
							migration: { title: "" }
						}
					},
					readonly: {
						title: "",
						description: {
							FACHVERFAHREN: "",
							STAMMDATEN: ""
						},
						button: {
							switch: {
								label: "",
								title: ""
							}
						}
					},
					deleting: {
						title: "",
						description: {
							FACHVERFAHREN: "",
							STAMMDATEN: ""
						}
					}
				},
				tooltip: {
					remark: "",
					deprecated: {
						unknown: "",
						outdated: "",
						discontinued: ""
					},
					invalid: { dockingPoint: "" },
					children: {
						error: "",
						warning: "",
						info: ""
					}
				}
			},
			fachverfahrenSaveRequest: {
				publish: {
					title: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					content: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					button: { submit: { label: "" } }
				},
				preview: {
					title: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					content: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					button: { submit: { label: "" } }
				}
			},
			forceLockRequest: {
				title: { FACHVERFAHREN: "", STAMMDATEN: "", PRINT_TEMPLATE: "" },
				content: {
					FACHVERFAHREN: "",
					STAMMDATEN: "",
					PRINT_TEMPLATE: ""
				},
				button: { submit: { label: "" }, refresh: { label: "" } }
			},
			fachverfahrenDeleteConfirmation: {
				delete: {
					title: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					content: {
						warning: "",
						consequences: {
							headline: "",
							fachverfahren: {
								FACHVERFAHREN: "",
								STAMMDATEN: ""
							},
							versions: {
								FACHVERFAHREN: "",
								STAMMDATEN: ""
							},
							data: {
								FACHVERFAHREN: "",
								STAMMDATEN: ""
							}
						},
						question: ""
					},
					button: { delete: { label: "" } }
				},
				deleteAll: {
					title: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					content: {
						warning: "",
						consequences: {
							headline: "",
							fachverfahren: {
								FACHVERFAHREN: "",
								STAMMDATEN: ""
							},
							previews: {
								FACHVERFAHREN: "",
								STAMMDATEN: ""
							},
							versions: {
								FACHVERFAHREN: "",
								STAMMDATEN: ""
							},
							data: {
								FACHVERFAHREN: "",
								STAMMDATEN: ""
							}
						},
						question: ""
					},
					button: { delete: { label: "" } }
				}
			},
			printTemplateDeleteConfirmation: {
				title: "",
				content: {
					warning: "",
					question: ""
				},
				button: { delete: { label: "" } }
			},
			moduleRemoveConfirmation: {
				title: "",
				message: "",
				button: { remove: { label: "" } }
			},
			printTemplateRemoveConfirmation: {
				title: "",
				message: "",
				button: { remove: { label: "" } }
			},
			internalApplicationModal: {
				title: "",
				content: {
					label: "",
					description: "",
					notice: "",
					personalLinksHint: "",
					button: {
						copy: { icon: { title: "" } },
						close: { title: "" }
					}
				}
			},
			fachverfahrenPublishConfirmationForm: {
				thresholdMessage: {
					label: {
						info: "",
						warning: "",
						error: ""
					},
					content: {
						info: "",
						warning: ""
					}
				}
			},
			moduleConfigurationForm: {
				global: {
					module: {
						text: ""
					}
				},
				externalEnumeration: {
					loading: "",
					missing: "",
					fachverfahren: ""
				},
				validationBar: {
					primary: "",
					secondary: "",
					button: { migration: { title: "" } }
				},
				print: {
					name: {
						add: "",
						edit: ""
					},
					option: {
						add: "Erstellen",
						edit: "Bearbeiten",
						remove: "Entfernen"
					}
				},
				mapping: {
					outdatedWarning: "",
					possiblyIncompatibleWarning: "",
					repeatabilityWarning: "",
					sourceRepeatMergeWarning: "",
					sourceRepeatSplitWarning: "",
					inaccuracyWarning: {
						fractionDigits: ""
					},
					fileUpload: {
						error: {
							message: {
								unprocessable: "",
								unknown: ""
							},
							title: ""
						}
					},
					component: {
						fachverfahrenFieldsLoadingStatus: {
							info: "",
							warning: "",
							empty: "",
							success: ""
						}
					}
				}
			},
			moduleSelectionModal: {
				title: "",
				message: { noCandidates: "" },
				button: { close: { title: "" } }
			},
			dockingPointSelectionModal: {
				title: "",
				description: "",
				select: { label: "" },
				button: { close: { title: "" }, selection: { label: "" } }
			},
			fachverfahrenClientModal: {
				description: {
					new: { FACHVERFAHREN: "", STAMMDATEN: "" },
					edit: { FACHVERFAHREN: "", STAMMDATEN: "" }
				}
			},
			printTemplateClientModal: {
				description: {
					new: "",
					edit: ""
				}
			},
			moduleNameModal: {
				description: ""
			},
			printTemplateNameModal: {
				description: ""
			},
			printTemplateCreateModal: {
				title: "",
				input: {
					name: { label: "" }
				},
				button: {
					confirm: { label: "" }
				}
			},
			printModelSelectionModal: {
				title: "",
				item: { internal: { label: "" } },
				button: {
					confirm: { label: "" }
				}
			},
			printTemplateShareModal: {
				title: "",
				input: {
					name: { label: "" }
				},
				button: {
					confirm: { label: "" }
				}
			},
			printModelEditor: {
				title: "",
				documentTitle: "",
				button: {
					fieldInformation: { label: "" },
					close: { title: "" }
				},
				modal: {
					edit: {
						title: "",
						message: ""
					},
					save: {
						button: { forceSave: { label: "" } }
					}
				}
			},
			moduleFieldTreePage: {
				title: "",
				columns: {
					field: "",
					path: "",
					type: "",
					values: ""
				},
				rootNode: {
					FACHVERFAHREN: "",
					STAMMDATEN: ""
				},
				button: { close: { title: "" } }
			}
		},
		general: {
			fachverfahren: {
				module: { unknown: "" },
				key: { label: "" },
				version: {
					editable: { label: "" },
					label: ""
				},
				lock: {
					title: "",
					description: {
						general: {
							FACHVERFAHREN: "",
							STAMMDATEN: "",
							PRINT_TEMPLATE: ""
						},
						specific: {
							FACHVERFAHREN: "",
							STAMMDATEN: "",
							PRINT_TEMPLATE: ""
						}
					},
					button: {
						unlock: {
							label: ""
						},
						refresh: {
							title: ""
						}
					}
				},
				toast: {
					general: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					specific: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					hints: {
						globalHints: {
							error: {
								FACHVERFAHREN: "",
								STAMMDATEN: ""
							},
							warning: {
								FACHVERFAHREN: "",
								STAMMDATEN: ""
							},
							info: {
								FACHVERFAHREN: "",
								STAMMDATEN: ""
							}
						},
						entityRelatedHints: {
							error: "",
							warning: "",
							info: ""
						}
					},
					button: {
						openFachverfahren: {
							label: {
								FACHVERFAHREN: "",
								STAMMDATEN: ""
							}
						},
						openPublish: { label: "" },
						openPreview: { label: "" },
						openInternalApplication: { label: "" }
					}
				}
			},
			error: {
				title: {
					critical: "",
					validation: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					save: {
						FACHVERFAHREN: "",
						STAMMDATEN: "",
						PRINT_TEMPLATE: ""
					},
					share: {
						PRINT_TEMPLATE: ""
					},
					import: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					copyInternalApplicationLink: "",
					action: ""
				},
				message: {
					unknown: "",
					popupBlocked: "",
					missingResources: "",
					maliciousResources: "",
					load: {
						general: {
							FACHVERFAHREN: "",
							STAMMDATEN: "",
							PRINT_TEMPLATE: ""
						},
						readonly: {
							FACHVERFAHREN: "",
							STAMMDATEN: "",
							PRINT_TEMPLATE: ""
						},
						locked: {
							FACHVERFAHREN: "",
							STAMMDATEN: "",
							PRINT_TEMPLATE: ""
						}
					},
					save: {
						general: {
							FACHVERFAHREN: "",
							STAMMDATEN: "",
							PRINT_TEMPLATE: ""
						},
						readonly: {
							FACHVERFAHREN: "",
							STAMMDATEN: "",
							PRINT_TEMPLATE: ""
						},
						outdated: {
							FACHVERFAHREN: "",
							STAMMDATEN: "",
							PRINT_TEMPLATE: ""
						},
						locked: {
							FACHVERFAHREN: "",
							STAMMDATEN: "",
							PRINT_TEMPLATE: ""
						}
					},
					share: {
						general: {
							PRINT_TEMPLATE: ""
						}
					},
					delete: {
						single: {
							FACHVERFAHREN: "",
							STAMMDATEN: ""
						},
						all: {
							FACHVERFAHREN: "",
							STAMMDATEN: ""
						}
					},
					loadWrongType: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					preview: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					update: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					publish: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					publishThreshold: "",
					import: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					persistPrintModel: "",
					persistPortalClients: "",
					loadModuleConfiguration: "",
					persistModuleConfiguration: "",
					buildDocumentModelModuleFailures: "",
					buildDocumentModel: "",
					loadPrintModel: "",
					uploadingMappingSourceFile: "",
					processingMappingSourceFile: "",
					processingMappingSourceSelect: "",
					loadingStammdaten: "",
					initializationFailed: { main: "", documentPathsTree: "" },
					forceLockFailed: "",
					copyInternalApplicationLink: "",
					restricted: {
						FACHVERFAHREN: "",
						STAMMDATEN: "",
						PRINT_TEMPLATE: ""
					},
					readonly: {
						FACHVERFAHREN: "",
						STAMMDATEN: "",
						PRINT_TEMPLATE: ""
					},
					locked: {
						FACHVERFAHREN: "",
						STAMMDATEN: "",
						PRINT_TEMPLATE: ""
					}
				},
				button: {
					label: {
						reload: "",
						forceSave: ""
					}
				}
			},
			warning: {
				title: {
					validation: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					import: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					loadModuleConfiguration: ""
				},
				message: {
					import: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					loadModuleConfiguration: ""
				}
			},
			info: {
				title: {
					action: "",
					validation: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					import: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					loadModuleConfiguration: ""
				},
				message: {
					preview: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					update: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					publish: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					delete: {
						single: {
							FACHVERFAHREN: "",
							STAMMDATEN: ""
						},
						all: {
							FACHVERFAHREN: "",
							STAMMDATEN: ""
						}
					},
					import: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					loadModuleConfiguration: "",
					migration: {
						unknown: "",
						losingConfiguration: "",
						losingSubModule: ""
					}
				}
			},
			success: {
				title: {
					action: "",
					validation: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					}
				},
				message: {
					preview: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					update: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					publish: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					download: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					export: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					import: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					persist: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					delete: {
						single: {
							FACHVERFAHREN: "",
							STAMMDATEN: "",
							PRINT_TEMPLATE: ""
						},
						all: {
							FACHVERFAHREN: "",
							STAMMDATEN: ""
						}
					},
					validate: {
						FACHVERFAHREN: "",
						STAMMDATEN: ""
					},
					copyInternalApplicationLink: "",
					share: {
						PRINT_TEMPLATE: ""
					}
				}
			}
		}
	},
	component: {
		errorBoundary: {
			report: { title: "" }
		},
		autocomplete: {
			hintTemplate: ""
		},
		modal: {
			general: {
				button: {
					close: { title: "" }
				}
			},
			reload: {
				title: "",
				message: "",
				button: { reload: { label: "" } }
			},
			logout: {
				title: "",
				message: "",
				caution: "",
				button: { logout: { label: "" } }
			},
			veto: {
				save: {
					title: "",
					message: "",
					button: {
						discard: { label: "" },
						commit: { label: "" }
					}
				},
				apply: {
					title: "",
					message: "",
					button: {
						discard: { label: "" },
						commit: { label: "" }
					}
				}
			},
			name: {
				title: { new: "", edit: "" },
				input: { label: "" },
				button: {
					confirm: { new: { label: "" }, edit: { apply: { label: "" }, save: { label: "" } } }
				}
			},
			client: {
				title: { new: "", edit: "" },
				button: {
					confirm: { new: { label: "" }, edit: { label: "" } }
				}
			}
		},
		button: {
			apply: { label: "" },
			save: { label: "" },
			close: { label: "" },
			cancel: { label: "" }
		},
		input: {
			clientSelection: {
				label: ""
			},
			printTemplateSelection: {
				label: "",
				groups: {
					system: { label: "", items: { default: { label: "" }, stammdaten: { label: "" } } },
					printTemplate: { label: "" },
					fachverfahren: { label: "" }
				}
			}
		},
		richTextEditor: {
			toolbar: { link: { title: "" }, image: { title: "" }, horizontalRule: { title: "" } },
			modal: { url: { title: "", input: { label: "" } } }
		}
	},
	general: {
		indication: {
			info: "",
			success: "",
			important: "",
			caution: "",
			warning: "",
			error: ""
		},
		type: {
			string: "",
			StringType: "",
			boolean: "",
			BooleanType: "",
			ConfirmType: "",
			enumeration: "",
			EnumerationType: "",
			date: "",
			DateType: "",
			DateTimeType: "",
			TimeType: "",
			NumberType: "",
			AttachmentType: ""
		}
	},
	attachment: {
		error: { "invalid-file": "", internal: "", unknown: "", "not-found": "", security: "" }
	}
};

initializeKeys(RESOURCE_KEYS);

type DeepPartial<T> = T extends object ? { [P in keyof T]?: DeepPartial<T[P]> } : T;

/** Creates a typing for a localization tree map with A12 localizable keys. */
export type LocalizationKeyTreeType = typeof RESOURCE_KEYS &
	DeepPartial<typeof CRUD_RESOURCE_KEYS> &
	DeepPartial<typeof DIRTY_HANDLING_RESOURCE_KEYS> &
	DeepPartial<typeof FRAME_RESOURCE_KEYS> &
	DeepPartial<typeof HETEROGENEITY_RESOURCE_KEYS> &
	DeepPartial<typeof LOCALE_RESOURCE_KEYS> &
	DeepPartial<typeof LOCALE_SELECT_RESOURCE_KEYS> &
	DeepPartial<typeof FORMENGINE_RESOURCE_KEYS> &
	DeepPartial<typeof OVERVIEWENGINE_RESOURCE_KEYS> &
	DeepPartial<typeof TREEENGINE_RESOURCE_KEYS>;
